import { AfterViewInit, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { UserService, OrganizationService, AppService, HelperService, FitnessService, FoodService, NutritionService, CardioProgramService, StorageService, TranslateConfigService } from 'src/app/services';
import { ModalController, IonSelect, AlertController, PopoverController, IonRouterOutlet, ToastController } from '@ionic/angular';
import { CardioProgramSettingsPage } from './cardio-program-settings/cardio-program-settings.page';
import { Organization, ICardioProgramDetail, CardioProgram, User, CardioCategory } from 'src/app/models';
import { NotesPage } from 'src/app/components/notes/notes.page';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import moment from 'src/app/utils/moment';
import * as Sentry from "@sentry/angular-ivy";
import { CardioCategorySelectComponent } from 'src/app/components/cardio-category-select/cardio-category-select.component';
import { getProgramDatePopoverSide, ProgramDatePopoverComponent } from 'src/app/components/program-date-popover/program-date-popover.component';
import { isAccountDeactivatedForBillingStatus } from 'src/app/shared/billing/organization-status-policy';

@Component({
  selector: 'app-cardio-program-builder',
  templateUrl: './cardio-program-builder.page.html',
  styleUrls: ['./cardio-program-builder.page.scss'],
})
export class CardioProgramBuilderPage implements OnInit, AfterViewInit {
  @Input() comparePaneEmbedded = false;
  @Output() comparePaneClose = new EventEmitter<any>();

  @Input() modalContext?: {
    cardioProgramUser?: string;
    id?: string;
    type?: 'user' | 'library';
    mode?: 'program' | 'category';
    duplicate?: string;
  };

  public actionSheet: HTMLIonActionSheetElement;
  public activeDay: any = { sessions: [] };
  public activeDayIndex: number;
  public activeCardioProgram: ICardioProgramDetail = new CardioProgram({ notes: [] }).cloneActive();
  public activeUser: User;
  public configSet: boolean = false;
  public dragDisabled = false;
  public hasDraft = false;
  public programUser: string;
  public programId: string;
  public programType: "user" | "library";
  public cardioProgram: CardioProgram;
  public organization: Organization;
  public programMode: "program" | "category";
  public saveMessageEnabled = true;
  public isPremium = false;
  public cardioTypes = {
    hiit: "Interval Training",
    liss: "Steady State",
    other: "Other"
  };
  public cardioTiming = {
    fasted: "Morning (fasted)",
    preTraining: "Pre-Training",
    postTraining: "Post-Training",
    other: "Other"
  };
  public versionHistory = [];
  public isDayDrawerOpen = false;

  @ViewChild(IonSelect, { read: IonSelect }) select: IonSelect;

  constructor(
    private alertController: AlertController,
    private appService: AppService,
    private cardioProgramService: CardioProgramService,
    private helperService: HelperService,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private popoverCtrl: PopoverController,
    @Optional() private routerOutlet: IonRouterOutlet,
    private storageService: StorageService,
    private toastCtrl: ToastController,
    private translateConfig: TranslateConfigService,
    private userService: UserService
  ) {
    this.activeUser = this.userService.getAppUser();
    this.organization = organizationService.getAppOrganization();
  }

  ngOnInit() {
    if (this.routerOutlet) {
      this.routerOutlet.swipeGesture = false;
    }
    this.storageService.get("cardioProgramBuilderMessageDisabled").then((val) => {
      if(val === true) {
        this.saveMessageEnabled = false;
      }
    }); 
  }

  /** Cardio builder is only entered via `CardioProgramBuilderModalService` + `modalContext`. */
  private buildProgramEntrySource(): Record<string, any> {
    return { ...((this.modalContext || {}) as Record<string, any>) };
  }

  async ionViewDidEnter() {
    if (this.comparePaneEmbedded) return;
    await this.runCardioBuilderEntry();
  }

  ngAfterViewInit(): void {
    if (this.comparePaneEmbedded) {
      void this.runCardioBuilderEntry();
    }
  }

  private dismissBuilder(data?: any): void {
    if (this.comparePaneEmbedded) {
      this.comparePaneClose.emit(data);
      return;
    }
    void this.modalCtrl.dismiss(data);
  }

  private async runCardioBuilderEntry() {
    if (isAccountDeactivatedForBillingStatus(this.organization?.status)) {
      await this.appService.showToastWithDuration(this.translateConfig.t('general.accountDeactivatedCannotEdit'), null, "danger");
      this.dismissBuilder();
      return;
    }

    const sourceParams = this.buildProgramEntrySource();
    this.programUser = sourceParams.cardioProgramUser || sourceParams.userId;
    this.programId = sourceParams.id || "add";
    this.programType = sourceParams.type || "user";
    this.programMode = sourceParams.mode || "program";

    if (!this.programId || this.programId === "add") {
      if (sourceParams.duplicate) {
        await this.appService.presentLoading();
        try {
          let cardioProgram = this.programType === "library"
            ? await this.cardioProgramService.getOrganizationCardioProgramAsPromise(this.organization.uid, sourceParams.duplicate)
            : await this.cardioProgramService.getCardioProgramAsPromise(this.programUser, sourceParams.duplicate);
          this.activeCardioProgram = cardioProgram.hasDraft() ? new CardioProgram(cardioProgram.cloneDraft()) : new CardioProgram(cardioProgram.cloneActive());
          this.activeCardioProgram.title = this.activeCardioProgram.title;
          delete this.activeCardioProgram.startDate;
          delete this.activeCardioProgram.endDate;
        } catch (error) {
          console.log(error);
          Sentry.captureException(error.originalError || error);
          await this.appService.showError("An error has occurred. Our support team has been notified.");
          this.goBack();
        } finally {
          setTimeout(() => {
            this.appService.hideLoading();
          }, 10);
        }
      }
      delete this.activeCardioProgram.createdBy;
      delete this.activeCardioProgram.creationDate;
      // Here we set premium for tracking users
      if(!this.organization || !this.organization.isUserTrainer(this.activeUser.uid)) {
        this.isPremium = true;
        this.activeCardioProgram.isPremium = true;
      }
      const defaultStartTime: string | null = this.organization?.getSetting("programs")?.defaultStartTime || null;
      const startMoment = (moment as any)();
      if (defaultStartTime) {
        const [hourStr] = defaultStartTime.split(":");
        const hour = parseInt(hourStr, 10);
        if (!Number.isNaN(hour)) {
          startMoment.hour(hour).minute(0).second(0).millisecond(0);
        }
      }
      this.activeCardioProgram.startDate = startMoment.toISOString();
      this.activeCardioProgram.startDateHidden = this.organization?.getSetting("programs")?.startDateHidden || false;
      this.activeCardioProgram.endDateHidden = this.organization?.getSetting("programs")?.endDateHidden || false;
      this.activeCardioProgram.notes = this.activeCardioProgram.notes && this.activeCardioProgram.notes.length > 0 ? this.activeCardioProgram.notes : (this.organization?.settings?.programs?.cardioPrograms?.defaultNotes || []);
      if (this.programMode === "category") {
        this.addDay("");
      }
      await this.showProgramOptions();
    } else {
      this.configSet = true;
      this.loadCardioProgram();
    }
  }

  ionViewDidLeave() {
    this.storageService.remove("exerciseSelectFilters");
  }

  async loadCardioProgram() {
    await this.appService.presentLoading();
    if(this.programType === "user") {
      this.cardioProgram = await this.cardioProgramService.getCardioProgramAsPromise(this.programUser, this.programId);
    } else {
        if(this.programMode === "program") {
          this.cardioProgram = await this.cardioProgramService.getOrganizationCardioProgramAsPromise(this.organization.uid, this.programId);
        } else {
          const category = await this.cardioProgramService.getOrganizationCardioCategoryAsPromise(this.organization.uid, this.programId);
          this.cardioProgram= new CardioProgram({
            title: category.title || null,
            programs: [{
              days: [{
                sessions: category.sessions || [],
                notes: category.notes || null,
              }]
            }]
          });
        }
    }
    let activeCardioProgram = this.cardioProgram.hasDraft() ? this.cardioProgram.cloneDraft() : this.cardioProgram.cloneActive();
    this.hasDraft = this.cardioProgram.published && this.cardioProgram.hasDraft();
    if (activeCardioProgram && activeCardioProgram.programs && activeCardioProgram.programs.length > 0 && activeCardioProgram.programs[0].days && activeCardioProgram.programs[0].days.length > 0) {
      this.activeCardioProgram = activeCardioProgram;
      this.setActiveDay(0);
      const toSave = this.helperService.copyObject(this.activeCardioProgram);
      this.versionHistory.push(toSave);
    }
    await this.appService.hideLoading();
  }

  async addSession() {
    this.activeDay.sessions = this.activeDay.sessions || [];
    this.activeDay.sessions.push({});
    this.saveCardioProgram();
  }

  removeSession(index) {
    this.activeDay.sessions.splice(index, 1);
    this.saveCardioProgram();
  }

  duplicateSession(session) {
    this.activeDay.sessions.push(this.helperService.copyObject(session));
    this.saveCardioProgram();
  }

  addDay(title: string, sessions?) {
    this.activeCardioProgram.programs[0].days.push({
      title: title,
      sessions: sessions || [],
    });
    this.saveCardioProgram();
  }

  setActiveDay(index: number) {
    this.activeDay = this.activeCardioProgram.programs[0].days[index];
    this.activeDayIndex = index;
  }

  openDayDrawer() {
    this.isDayDrawerOpen = !this.isDayDrawerOpen;
  }

  onDaySelected(index: number) {
    this.setActiveDay(index);
  }

  selectDayForActions(index: number): void {
    this.setActiveDay(index);
  }

  editDayFromDrawer(index: number): void {
    this.selectDayForActions(index);
    this.editDayTitle();
  }

  duplicateDayFromDrawer(day: any, index: number): void {
    this.selectDayForActions(index);
    this.duplicateDay(day, index);
  }

  saveCategoryToLibraryFromDrawer(day: any, index: number): void {
    this.selectDayForActions(index);
    this.saveCategoryToLibrary(day);
  }

  deleteDayFromDrawer(index: number): void {
    this.selectDayForActions(index);
    this.deleteDay(index);
  }

  async newDay() {
    const alert = await this.alertController.create({
      header: 'Cardio Day Title',
      inputs: [
        {
          name: 'dayTitle',
          type: 'text',
          placeholder: 'e.g. Workout Day'
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            if (values.dayTitle) {
              this.addDay(values.dayTitle);
              this.setActiveDay(this.activeCardioProgram.programs[0].days.length - 1);
              this.saveCardioProgram();
            } else {
              const failAert = await this.alertController.create({
                header: 'Cardio Day Title Missing',
                message: 'You must enter a title',
                buttons: ['OK']
              });
              await failAert.present();
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async editDayTitle() {
    let value = this.activeDay.title;
    const alert = await this.alertController.create({
      header: 'Edit Category Title',
      inputs: [
        {
          name: 'planTitle',
          type: 'text',
          value: value,
          placeholder: `e.g. Non Workout Days`,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let planTitle = values.planTitle
            if (planTitle) {
              this.activeDay.title = planTitle;
              this.saveCardioProgram();
            } else {
              return
            }
          }
        }
      ]
    });
    await alert.present();
  }

  getProgramSettings() {
    return {
      title: this.activeCardioProgram.title || null,
      startDate: this.activeCardioProgram.startDate || null,
      startDateHidden: this.helperService.valueExists(this.activeCardioProgram.startDateHidden)? this.activeCardioProgram.startDateHidden : false,
      endDate: this.activeCardioProgram.endDate || null,
      endDateHidden: this.helperService.valueExists(this.activeCardioProgram.endDateHidden)? this.activeCardioProgram.endDateHidden : false,
      programs: this.activeCardioProgram.programs || [],
    }
  }

  setProgramSettings(obj) {
    Object.assign(this.activeCardioProgram, obj);
    if (isNaN(this.activeDayIndex)) {
      this.setActiveDay(0);
    }
  }

  async showProgramOptions() {
    let action = this.configSet ? "edit" : "add";
    const modal = await this.modalCtrl.create({
      component: CardioProgramSettingsPage,
      backdropDismiss: false,
      componentProps: {
        action: action,
        cardioProgram: this.getProgramSettings(),
        type: this.programType,
        premium: this.isPremium,
        mode: this.programMode,
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === 'submitted') {
      this.setProgramSettings(data.data);
      this.configSet = true;
      this.saveCardioProgram();
    } else if (!this.configSet) {
      setTimeout(() => {
        this.goBack();
      }, 500);
    } else { }
  }

  async showNotes() {
    let notes = this.activeCardioProgram.notes;
    const modal = await this.modalCtrl.create({
      component: NotesPage,
      backdropDismiss: false,
      componentProps: {
        defaultNotes: this.organization?.settings?.programs?.cardioPrograms?.defaultNotes,
        action: "edit",
        notes: notes
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "submitted") {    
      this.activeCardioProgram.notes = data.data;
      this.saveCardioProgram();
    }
  }

  disableSaveMessage() {
    this.storageService.set("cardioProgramBuilderMessageDisabled", true);
    this.saveMessageEnabled = false;
  }

  async showSaveMessage() {
    if(!this.saveMessageEnabled) {
      return
    }
    const toast = await this.toastCtrl.create({
      message: "Your cardio program has been automatically saved",
      buttons: [
        {
          text: "Don't show again",
          role: 'cancel',
          handler: () => {
            this.disableSaveMessage();
          }
        }
      ],
      position: 'bottom',
      color: "success",
      duration: 3000,
    });
    toast.present();
  }

  async saveCardioProgram() {
    try {
      let res;
      const toSave = this.helperService.copyObject(this.activeCardioProgram);
      this.versionHistory.push(toSave);
      if(this.programType === "user") {
        res = await this.cardioProgramService.saveUserCardioProgram(this.organization?.uid, this.programUser, this.programId, toSave, this.activeUser.uid);
      } else {
        if(this.programMode === "category") {
          return
        }
        res = await this.cardioProgramService.saveOrganizationCardioProgram(this.organization.uid, this.programId, toSave, this.activeUser.uid);
      }
      if (this.programId === "add") {
        this.programId = res.key;
      }
      this.showSaveMessage();
      if(this.cardioProgram?.published) {
        this.hasDraft = true;
      }
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    }
  }

  async publishCardioProgram() {
    await this.appService.presentLoading();
    try {
      let programValue;
      if(this.programType === "user") {
        programValue = await this.cardioProgramService.publishUserCardioProgram(this.organization?.uid, this.programUser, this.programId, this.activeCardioProgram, this.activeUser.uid);
      } else {
        if(this.programMode === "program") {
          programValue = await this.cardioProgramService.publishOrganizationCardioProgram(this.organization.uid, this.programId, this.activeCardioProgram, this.activeUser.uid);
        } else {
          programValue = await this.cardioProgramService.publishOrganizationCardioCategory(this.organization.uid, this.programId, {title: this.activeCardioProgram.title, sessions: this.activeDay?.sessions || [], notes: this.activeDay.notes || null}, this.activeUser.uid);
        }
      }
      if (this.programId == "add") {
        this.programId = programValue.key;
      }
      
      this.dismissBuilder({ status: 'published', programId: this.programId });

      this.appService.showToastWithDuration("Cardio Program Published", null, "success");
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    } finally {
      await this.appService.hideLoading();
    }
  }


  undoRevision() {
    const historyIndex = this.versionHistory.length - 1;
    this.activeCardioProgram = this.versionHistory[historyIndex - 1];
    this.setActiveDay(this.activeDayIndex);
    this.versionHistory.splice(historyIndex -1, 2);
    this.saveCardioProgram();
  }

  async revertDraft() {
    let activeCardioProgram = this.cardioProgram.cloneActive();
    if (activeCardioProgram && activeCardioProgram.programs && activeCardioProgram.programs.length > 0 && activeCardioProgram.programs[0].days && activeCardioProgram.programs[0].days.length > 0) {
      this.activeCardioProgram = activeCardioProgram;
      this.setActiveDay(0);
      const toSave = this.helperService.copyObject(this.activeCardioProgram);
      this.versionHistory = [toSave];
    }
    try {
      if(this.programType === "user") {
        await this.cardioProgramService.publishUserCardioProgram(this.organization.uid, this.programUser, this.programId, this.activeCardioProgram, this.activeUser.uid, false);
      } else {
        await this.cardioProgramService.publishOrganizationCardioProgram(this.organization.uid, this.programId, this.activeCardioProgram, this.activeUser.uid);
      }
      this.hasDraft = false;
      this.appService.showToastWithDuration("Cardio Program Successfully Reverted", null, "success");
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    }
  }

  isTrainer() {
    return this.activeUser && this.organization.isUserTrainer(this.activeUser.uid);
  }

  reorderSession(event) {
    const itemMove = this.activeDay.sessions.splice(event.detail.from, 1)[0];
    this.activeDay.sessions.splice(event.detail.to, 0, itemMove);
    this.saveCardioProgram();
    event.detail.complete();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.activeCardioProgram.programs[0].days, event.previousIndex, event.currentIndex);
    this.setActiveDay(event.currentIndex);
    this.saveCardioProgram();
  }

  deleteDay(index: number) {
    this.activeCardioProgram.programs[0].days.splice(index, 1);
    this.setActiveDay(this.activeCardioProgram.programs[0].days[index] ? index : 0);
    this.saveCardioProgram();
  }

  duplicateDay(day: any, currentIndex) {
    const toCopy = this.helperService.copyObject(day);
    toCopy.title = day.title;
    this.activeCardioProgram.programs[0].days.splice(currentIndex + 1, 0, toCopy);
    this.setActiveDay(currentIndex + 1);
    this.saveCardioProgram();
  }

    async saveCategoryToLibrary(supplementCategory: Partial<CardioCategory>) {
      if(this.organization?.uid) {
        if(["standard",  "pro", "platinum"].indexOf(this.organization.plan) > -1) {
          const toSave: Partial<CardioCategory> = {
            sessions: supplementCategory.sessions || null,
            title: supplementCategory.title || null,
            notes: supplementCategory.notes || null
          };
          await this.cardioProgramService.publishOrganizationCardioCategory(this.organization.uid, null, toSave, this.activeUser.uid);
          await this.appService.showToastWithDuration("Category successfully saved to library", null, "success")
        } else {
          this.appService.showUpgradeModal();
        }
      }
    }

  async selectCategory() {
    if(["standard",  "pro", "platinum"].indexOf(this.organization.plan) > -1) {
      const modal = await this.modalCtrl.create({
        component: CardioCategorySelectComponent,
        componentProps: {}
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === "selected" && data.data) {
        this.addDay(data.data.title || null, data.data.sessions || []);
        this.setActiveDay(this.activeCardioProgram.programs[0].days.length - 1);
        this.saveCardioProgram();
      }
    } else {
      this.appService.showUpgradeModal();
    }
  }
  

  disableDrag() {
    this.dragDisabled = true;
  }

  enableDrag() {
    this.dragDisabled = false;
  }

  goBack() {
    this.dismissBuilder();
  }

  async editDate(event, type: "startDate" | "endDate") {
    const popover = await this.popoverCtrl.create({
      component: ProgramDatePopoverComponent,
      event: event,
      cssClass: 'popover-full',
      side: getProgramDatePopoverSide(event),
      componentProps: {
        initial: this.activeCardioProgram[type],
        clear: type === "endDate",
        includeTime: true,
        dateType: type,
      }
    });
    await popover.present();
    const { role, data } = await popover.onDidDismiss();
    if(role === "cleared" || role === "set") {
      const toUpdate = data || null;
      this.activeCardioProgram[type] = toUpdate;
      this.saveCardioProgram();
    }
  }
}
